// Green Screen config shared over the socket's system_message, which the server broadcasts to
// everyone in the room with whatever payload it was sent. Hosts and sponsors keep one config
// for the session between them, and the OBS overlay (a guest opened on the overlay link) shows
// the captions with it. Anyone in the room can send a system_message, so everything received
// here is checked: the sender against the room's users, the config field by field.

export const GREEN_SCREEN_MESSAGE = Object.freeze({
  config: 'green_screen_config',
  request: 'green_screen_config_request',
});

export const GREEN_SCREEN_MESSAGE_VERSION = 1;

// The overlay joins as an ordinary guest under this name; that is how the room tells overlays
// apart from people (e.g. to count the connected ones).
export const OVERLAY_GUEST_NAME = 'Captions overlay';

// The OBS canvas shapes the captions can be set up for, with the canvas size each is laid out at.
// Font sizes are in canvas pixels, so a size reads the same on any of them.
export const CANVAS_FORMATS = Object.freeze({
  '16:9': Object.freeze({ width: 1920, height: 1080 }),
  '9:16': Object.freeze({ width: 1080, height: 1920 }),
  '1:1': Object.freeze({ width: 1080, height: 1080 }),
  '4:3': Object.freeze({ width: 1440, height: 1080 }),
});
export const DEFAULT_CANVAS_FORMAT = '16:9';

export const canvasFor = (format) => CANVAS_FORMATS[Object.prototype.hasOwnProperty.call(CANVAS_FORMATS, format) ? format : DEFAULT_CANVAS_FORMAT];

// Hosts send at most this often while dragging or holding a key; the last value always goes out.
export const CONFIG_SEND_INTERVAL_MS = 200;

const WIDTHS = ['wider', 'narrower'];
const ALIGNS = ['left', 'center', 'right'];
const FONTS = ['Inter', 'Noto Serif'];

const isHexColor = (value) => typeof value === 'string' && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
const inRange = (value, min, max) => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;

// Keeps only known fields with valid values; anything else is dropped rather than applied.
export function sanitizeConfig(raw, { minFontSize = 12, maxFontSize = 150 } = {}) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const config = {};
  if (inRange(raw.fontSize, minFontSize, maxFontSize)) config.fontSize = Math.round(raw.fontSize);
  if ([1, 2, 3].includes(raw.lines)) config.lines = raw.lines;
  if (WIDTHS.includes(raw.width)) config.width = raw.width;
  if (ALIGNS.includes(raw.align)) config.align = raw.align;
  if (FONTS.includes(raw.font)) config.font = raw.font;
  if (isHexColor(raw.background)) config.background = raw.background;
  if (inRange(raw.x, 0, 1)) config.x = raw.x;
  if (inRange(raw.y, 0, 1)) config.y = raw.y;
  if (typeof raw.hidden === 'boolean') config.hidden = raw.hidden;
  if (typeof raw.format === 'string' && Object.prototype.hasOwnProperty.call(CANVAS_FORMATS, raw.format)) config.format = raw.format;

  return Object.keys(config).length ? config : null;
}

// Last change wins: a higher revision, and for two changes made at once the sender id breaks
// the tie the same way on every client.
export function isNewerRevision(candidate, current) {
  if (candidate.rev !== current.rev) {
    return candidate.rev > current.rev;
  }
  return String(candidate.from || '') > String(current.from || '');
}

// The server may hand the payload back as is or wrapped; accept both.
export function unwrapSystemMessage(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const message = data.type ? data : (data.payload || data.message || data.data);
  if (!message || typeof message !== 'object' || message.v !== GREEN_SCREEN_MESSAGE_VERSION) {
    return null;
  }
  return Object.values(GREEN_SCREEN_MESSAGE).includes(message.type) ? message : null;
}

// The session's last config, kept per room so a host who comes back (or reloads) picks up where
// the session was rather than from their own preferences.
const sessionKey = (roomCode) => `greenScreenSession:${roomCode}`;

export function loadSessionConfig(roomCode) {
  try {
    const saved = JSON.parse(localStorage.getItem(sessionKey(roomCode)) || 'null');
    if (!saved || typeof saved.rev !== 'number') return null;
    const config = sanitizeConfig(saved.config);
    return config ? { rev: saved.rev, from: saved.from || '', config } : null;
  } catch (e) {
    return null;
  }
}

export function saveSessionConfig(roomCode, entry) {
  try {
    localStorage.setItem(sessionKey(roomCode), JSON.stringify(entry));
  } catch (e) {
    // Storage full or blocked: the session config just isn't remembered.
  }
}

// The config snapshot carried in the overlay link, for when no host is online to answer the
// overlay's request. URL-safe base64 of the JSON.
export function encodeConfigParam(config) {
  const json = JSON.stringify(config);
  const base64 = btoa(unescape(encodeURIComponent(json)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeConfigParam(value, limits) {
  if (!value || typeof value !== 'string') {
    return null;
  }
  try {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(escape(atob(base64)));
    return sanitizeConfig(JSON.parse(json), limits);
  } catch (e) {
    return null;
  }
}

// Calls fn at most once per interval; a call inside the interval is held and sent at its end
// with the latest arguments. flush() sends a held call right away.
export function createThrottle(fn, intervalMs) {
  let lastRun = 0;
  let timer = null;

  const run = () => {
    timer = null;
    lastRun = Date.now();
    fn();
  };

  const throttled = () => {
    const wait = intervalMs - (Date.now() - lastRun);
    if (wait <= 0) {
      if (timer) {
        clearTimeout(timer);
      }
      run();
    } else if (!timer) {
      timer = setTimeout(run, wait);
    }
  };

  throttled.flush = () => {
    if (timer) {
      clearTimeout(timer);
    }
    run();
  };

  throttled.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return throttled;
}
